import React, { useMemo, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import {
  DataGrid,
  gridClasses,
  GridColDef,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { LightTooltip } from "../../../../../../components/LightTooltip";
import { lightScrollbarStyle } from "../../../../../../styles/lightScrollbarStyle";
import { EmptyState } from "../../../../components/EmptyState";
import { PieChartIcon } from "../../../../../../components/icons/FAI/PieChartIcon";
import { DecisionMakerIcon } from "../../../../../../components/icons/FAI/DecisionMakerIcon";
import { InfluencerIcon } from "../../../../../../components/icons/FAI/InfluencerIcon";
import {
  DepartmentRecord,
  FunctionalAreaRoleRecord,
} from "../../../../../../legacy/queries/FAI";
import { FaiByLocationModal } from "./FaiByLocationModal";
import { HgProduct } from "../../../../../../types/taxonomy";

interface FaiByProductTableProps {
  data?: DepartmentRecord[];
  loading: boolean;
  product: Pick<HgProduct, "id" | "name">;
}

interface BaseRow {
  id: string;
  isSkeleton: boolean;
  department?: string;
  departmentDisplayName?: string;
  decisionMaker?: boolean;
  influencer?: boolean;
  productSignalStrength?: string;
  isRole?: boolean;
  roleName?: string;
  roleId?: string;
  roleSignalStrength?: number;
  parentDepartment?: string;
  hasRoles?: boolean;
}

type SkeletonRow = BaseRow & { isSkeleton: true };
type DataRow = BaseRow & { isSkeleton: false };
type TableRow = DataRow | SkeletonRow;

const HeaderWithTooltip: React.FC<{
  title: string;
  tooltip?: string | React.ReactNode;
}> = React.memo(({ title, tooltip }) => {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Typography variant="subtitle2" fontWeight="bold">
        {title}
      </Typography>
      {tooltip && (
        <LightTooltip
          title={<Typography variant="body2">{tooltip}</Typography>}
        >
          <IconButton size="small" sx={{ p: 0 }}>
            <HelpOutlineIcon sx={{ fontSize: 14, color: "text.secondary" }} />
          </IconButton>
        </LightTooltip>
      )}
    </Stack>
  );
});

HeaderWithTooltip.displayName = "HeaderWithTooltip";

const BuyerRoleCell: React.FC<{
  decisionMaker: boolean;
  influencer: boolean;
}> = React.memo(({ decisionMaker, influencer }) => {
  if (!decisionMaker && !influencer) {
    return (
      <Typography
        variant="body2"
        sx={{ fontStyle: "italic", color: "text.secondary" }}
      >
        No buyer role detected
      </Typography>
    );
  }

  return (
    <Stack spacing={0.5}>
      {decisionMaker && (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <DecisionMakerIcon width={16} height={16} />

          <Typography variant="body2">Decision Maker</Typography>
        </Stack>
      )}
      {influencer && (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <InfluencerIcon width={16} height={14} />

          <Typography variant="body2">Influencer</Typography>
        </Stack>
      )}
    </Stack>
  );
});

BuyerRoleCell.displayName = "BuyerRoleCell";

export const FaiByProductTable: React.FC<FaiByProductTableProps> = React.memo(
  ({ data, loading, product }) => {
    const [locationModalOpen, setLocationModalOpen] = useState(false);
    const [selectedDepartment, setSelectedDepartment] = useState<
      string | undefined
    >();
    const [selectedRole, setSelectedRole] = useState<string | undefined>();
    const [expandedDepartments, setExpandedDepartments] = useState<Set<string>>(
      new Set()
    );

    const hasResults = Boolean(data && data.length > 0);
    const showEmptyState = !loading && !hasResults;

    const emptyStateDescription = useMemo(() => {
      if (product.name) {
        return `No department data found for ${product.name}. Try selecting a different product or refine the filters.`;
      }
      return "No department data found. Please select a product.";
    }, [product.name]);

    const toggleDepartment = useCallback((departmentId: string) => {
      setExpandedDepartments((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(departmentId)) {
          newSet.delete(departmentId);
        } else {
          newSet.add(departmentId);
        }
        return newSet;
      });
    }, []);

    const rows: TableRow[] = useMemo(() => {
      if (loading) {
        return Array.from({ length: 10 }).map(
          (_, index) =>
            ({
              id: `skeleton-${index}`,
              isSkeleton: true,
            }) as SkeletonRow
        );
      }

      const transformedRows: TableRow[] = [];

      data?.forEach((dept: DepartmentRecord) => {
        const roles = dept.functionalAreaRoles || [];
        const hasRoles = roles.length > 0;
        // Add department row
        const deptRow: DataRow = {
          id: dept.department,
          isSkeleton: false,
          department: dept.department,
          departmentDisplayName: dept.departmentDisplayName,
          decisionMaker: dept.decisionMaker,
          influencer: dept.influencer,
          productSignalStrength: dept.productSignalStrength,
          isRole: false,
          hasRoles,
        };
        transformedRows.push(deptRow);

        // Add role rows if department is expanded
        if (hasRoles && expandedDepartments.has(dept.department)) {
          roles.forEach((role: FunctionalAreaRoleRecord) => {
            const roleRow: DataRow = {
              id: `${dept.department}-${role.functionalAreaRole}`,
              isSkeleton: false,
              isRole: true,
              roleName: role.functionalAreaRoleDisplayName,
              roleId: role.functionalAreaRole,
              roleSignalStrength: role.functionalAreaRoleSignalStrength,
              decisionMaker: role.decisionMaker,
              influencer: role.influencer,
              parentDepartment: dept.department,
              department: dept.department,
              departmentDisplayName: dept.departmentDisplayName,
            };
            transformedRows.push(roleRow);
          });
        }
      });

      return transformedRows;
    }, [data, loading, expandedDepartments]);

    const columnsDefinition: GridColDef<TableRow>[] = useMemo(
      () => [
        {
          field: "departmentDisplayName",
          headerName: "Departments",
          flex: 1,
          sortable: false,
          renderHeader: () => <HeaderWithTooltip title="Departments" />,
          renderCell: (params: GridRenderCellParams<TableRow>) => {
            if (params.row.isSkeleton) {
              return <Skeleton variant="text" width="80%" />;
            }
            const rowData = params.row as DataRow;

            if (rowData.isRole) {
              // Role row - indented with no expand icon
              return (
                <Box sx={{ pl: 5 }}>
                  <Tooltip title={rowData.roleName} placement="bottom-start">
                    <Typography variant="body2" noWrap>
                      {rowData.roleName}
                    </Typography>
                  </Tooltip>
                </Box>
              );
            }

            // Department row
            return (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                {rowData.hasRoles ? (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleDepartment(rowData.department!);
                    }}
                    sx={{ p: 0 }}
                  >
                    {expandedDepartments.has(rowData.department!) ? (
                      <KeyboardArrowDownIcon fontSize="small" />
                    ) : (
                      <KeyboardArrowRightIcon fontSize="small" />
                    )}
                  </IconButton>
                ) : (
                  <Box sx={{ width: 24 }} />
                )}
                <Tooltip
                  title={rowData.departmentDisplayName}
                  placement="bottom-start"
                >
                  <Typography variant="body2" noWrap>
                    {rowData.departmentDisplayName}
                  </Typography>
                </Tooltip>
              </Stack>
            );
          },
        },
        {
          field: "buyerRole",
          headerName: "Buyer Role",
          flex: 1,
          width: 200,
          sortable: false,
          renderHeader: () => (
            <HeaderWithTooltip
              title="Buyer Role"
              tooltip={
                <div>
                  <p>
                    Buyer Role refers to individuals who hold positions in
                    decision-making processes and possess the power to influence
                    critical choices within a company.
                  </p>
                  <p>
                    <b>Decision Makers:</b> These individuals occupy high-level
                    positions such as C-Level/VP roles, Directors, Department
                    Heads, or Company Founders.
                  </p>
                  <p>
                    <b>Influencers:</b> These individuals have influence within
                    their specific domains. They can include Associate VP roles,
                    Managers, Team Leads, or HR Business Partners.
                  </p>
                </div>
              }
            />
          ),
          renderCell: (params) => {
            if (params.row.isSkeleton) {
              return <Skeleton variant="text" width="80%" />;
            }
            const rowData = params.row as DataRow;
            return (
              <BuyerRoleCell
                decisionMaker={rowData.decisionMaker || false}
                influencer={rowData.influencer || false}
              />
            );
          },
        },
        {
          field: "usageShare",
          headerName: "Usage Share",
          width: 150,
          sortable: false,
          renderHeader: () => (
            <HeaderWithTooltip
              title="Usage Share"
              tooltip="This column shows the percentage of this product's usage coming from the given department."
            />
          ),
          renderCell: (params) => {
            if (params.row.isSkeleton) {
              return (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Skeleton variant="circular" width={32} height={32} />
                  <Skeleton variant="text" width="40px" />
                </Stack>
              );
            }
            const rowData = params.row as DataRow;

            if (rowData.isRole && rowData.roleSignalStrength !== undefined) {
              // For role rows, calculate percentage within the department
              const parentDept = data?.find(
                (d) => d.department === rowData.parentDepartment
              );
              const departmentRolesTotal =
                parentDept?.functionalAreaRoles?.reduce(
                  (sum, role) => sum + role.functionalAreaRoleSignalStrength,
                  0
                ) || 1;

              const percentage =
                (rowData.roleSignalStrength / departmentRolesTotal) * 100;

              return (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PieChartIcon percentage={percentage} size={32} />
                  <Typography variant="body2" fontWeight="medium">
                    {percentage.toFixed(2)}%
                  </Typography>
                </Stack>
              );
            }

            // Department row
            const percentage = parseFloat(rowData.productSignalStrength || "0");
            return (
              <Stack direction="row" alignItems="center" spacing={1}>
                <PieChartIcon percentage={percentage} size={32} />
                <Typography variant="body2" fontWeight="medium">
                  {percentage.toFixed(2)}%
                </Typography>
              </Stack>
            );
          },
        },
        {
          field: "locations",
          headerName: "Locations",
          width: 150,
          sortable: false,
          renderHeader: () => <HeaderWithTooltip title="Locations" />,
          renderCell: (params) => {
            if (params.row.isSkeleton) {
              return <Skeleton variant="text" width="80%" />;
            }
            return (
              <Link
                component="button"
                variant="body2"
                sx={{
                  textDecoration: "underline",
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "none",
                  },
                }}
                onClick={() => {
                  const rowData = params.row as DataRow;
                  setSelectedDepartment(rowData.departmentDisplayName);
                  setSelectedRole(
                    rowData.isRole ? rowData.roleName : undefined
                  );
                  setLocationModalOpen(true);
                }}
              >
                View Top Locations
              </Link>
            );
          },
        },
      ],
      [data, expandedDepartments, toggleDepartment]
    );

    const titleText = useMemo(() => {
      if (!product.name) return "";
      if (hasResults) {
        const count = data?.length || 0;
        if (count === 10) {
          return `Top 10 departments using ${product.name}`;
        }
        return `${count} departments using ${product.name}`;
      }
      return `0 departments using ${product.name}`;
    }, [product.name, hasResults, data?.length]);

    return (
      <Box>
        {product.name && <Typography variant="h5">{titleText}</Typography>}

        {showEmptyState ? (
          <EmptyState
            title="No department data found"
            description={emptyStateDescription}
            height={600}
          />
        ) : (
          <Box sx={{ height: 520, width: "100%", marginLeft: -1 }}>
            <DataGrid
              rows={rows}
              columns={columnsDefinition}
              hideFooter
              rowHeight={45}
              getRowId={(row) => row.id}
              getRowClassName={(params) => {
                const row = params.row as DataRow;
                return row.isRole ? "role-row" : "";
              }}
              disableColumnFilter
              disableRowSelectionOnClick
              disableColumnMenu
              disableColumnResize
              disableColumnSelector
              sx={{
                [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]:
                  {
                    outline: "none",
                  },
                [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
                  {
                    outline: "none",
                  },
                [`& .${gridClasses.columnSeparator}`]: {
                  [`&:not(.${gridClasses["columnSeparator--resizable"]})`]: {
                    display: "none",
                  },
                },
                "& .MuiDataGrid-columnHeader": {
                  borderBottom: "none !important",
                },
                "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer":
                  {
                    "& .MuiIconButton-root": {
                      padding: 0,
                    },
                    "& .MuiTouchRipple-root": {
                      display: "none",
                    },
                  },
                "&, [class^=MuiDataGrid]": { border: "none" },
                "& .MuiDataGrid-cell": {
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiDataGrid-filler": {
                  display: "none",
                },
                ".MuiDataGrid-scrollbarFiller": {
                  display: "none",
                },
                "& .MuiDataGrid-scrollbar--vertical": {
                  ...lightScrollbarStyle,
                },
                "& .MuiDataGrid-row": {
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                },
              }}
            />
          </Box>
        )}

        {product.id && selectedDepartment && (
          <FaiByLocationModal
            open={locationModalOpen}
            onClose={() => setLocationModalOpen(false)}
            product={product}
            department={selectedDepartment}
            role={selectedRole}
          />
        )}
      </Box>
    );
  }
);

FaiByProductTable.displayName = "FaiByProductTable";
